import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { dietService } from '../../services/dietService';
import { ingredientService } from '../../services/ingredientService';
import { Recipe, CreateRecipeRequest, UpdateRecipeRequest, Menu } from '../../types/diet';
import { MeasureUnit } from '../../types/ingredient';

interface Props {
  recipe?: Recipe | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RecipeModal: React.FC<Props> = ({ recipe, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [selectedDietId, setSelectedDietId] = useState<string>('');
  const [selectedMenuId, setSelectedMenuId] = useState<string>((recipe as any)?.menuId || '');
  const [selectedDietIngredients, setSelectedDietIngredients] = useState<string[]>([]);
  const isEditing = !!recipe;

  // Dropdown data
  const { data: dietsData } = useQuery({ queryKey: ['diets', 'for-select'], queryFn: () => dietService.getDiets({ page: 1, limit: 1000 }) });
  const { data: menusData } = useQuery({
    queryKey: ['menus', 'for-diet', selectedDietId],
    queryFn: () => dietService.getMenus({ page: 1, limit: 1000, dietId: selectedDietId }),
    enabled: !!selectedDietId
  });
  const { data: ingredientsData } = useQuery({ queryKey: ['ingredients', 'for-select'], queryFn: () => ingredientService.getIngredients({ page: 1, limit: 1000 }) });
  const { data: measureUnitsData } = useQuery({ queryKey: ['measure-units', 'for-select'], queryFn: () => ingredientService.getMeasureUnits() });

  // Fetch selected diet data
  const { data: selectedDietData } = useQuery({
    queryKey: ['diet', selectedDietId],
    queryFn: () => dietService.getDiet(selectedDietId as string),
    enabled: !!selectedDietId,
  });

  // Load full recipe when editing to get items
  const { data: fullRecipeData, isLoading: isLoadingRecipe } = useQuery({
    queryKey: ['recipe', recipe?.id],
    queryFn: () => dietService.getRecipe(recipe!.id),
    enabled: isEditing && !!recipe?.id,
  });

  const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch } = useForm<CreateRecipeRequest>({
    defaultValues: {
      name: recipe?.name || '',
      description: recipe?.description || '',
      instructions: recipe?.instructions || '',
      menuId: (recipe as any)?.menuId || '',
      totalWeight: (recipe as any)?.totalWeight ?? 0,
      totalMeasureUnitId: (recipe as any)?.totalMeasureUnitId || (recipe as any)?.totalMeasureUnit?.id || '',
      status: recipe?.status || 'ACTIVE',
      recipeItems: [],
    }
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'recipeItems' });
  const watchRecipeItems = watch('recipeItems');

  // Initialize diet/menu from recipe prop when modal opens
  useEffect(() => {
    if (isEditing && recipe) {
      const r: any = recipe;
      console.log('[RecipeModal] Initializing from recipe prop:', r);

      // Try to get dietId from the recipe's menu
      if (r.menu?.diet?.id) {
        console.log('[RecipeModal] Setting initial dietId from recipe.menu.diet.id:', r.menu.diet.id);
        setSelectedDietId(r.menu.diet.id);
      } else if (r.menu?.dietId) {
        console.log('[RecipeModal] Setting initial dietId from recipe.menu.dietId:', r.menu.dietId);
        setSelectedDietId(r.menu.dietId);
      }

      if (r.menuId) {
        console.log('[RecipeModal] Setting initial menuId:', r.menuId);
        setSelectedMenuId(r.menuId);
      }
    }
  }, [isEditing, recipe?.id]); // Only run when recipe changes

  // When full recipe loads for editing, populate items and set diet
  useEffect(() => {
    if (isEditing && fullRecipeData?.recipe) {
      const r: any = fullRecipeData.recipe;
      console.log('[RecipeModal] ========== LOADING RECIPE FOR EDIT ==========');
      console.log('[RecipeModal] Full recipe data:', r);
      console.log('[RecipeModal] Recipe menuId:', r.menuId);
      console.log('[RecipeModal] Recipe menu object:', r.menu);
      console.log('[RecipeModal] Recipe items:', r.recipeItems);

      const items = (r.recipeItems || []).map((it: any) => ({
        ingredientId: it.ingredientId || it.ingredient?.id || '',
        quantity: it.quantity || 0,
        measureUnitId: it.measureUnitId || it.measureUnit?.id || undefined,
      }));

      console.log('[RecipeModal] Mapped items:', items);

      // Set diet from menu - try multiple paths
      const dietId = r.menu?.diet?.id || r.menu?.dietId;
      console.log('[RecipeModal] Extracted dietId:', dietId);
      console.log('[RecipeModal] Menu object structure:', JSON.stringify(r.menu, null, 2));

      if (dietId) {
        console.log('[RecipeModal] Setting selectedDietId to:', dietId);
        setSelectedDietId(dietId);
      } else {
        console.warn('[RecipeModal] ⚠️ No dietId found in menu object!');
      }

      if (r.menuId) {
        console.log('[RecipeModal] Setting selectedMenuId to:', r.menuId);
        setSelectedMenuId(r.menuId);
      }

      reset({
        name: r.name || '',
        description: r.description || '',
        instructions: r.instructions || '',
        menuId: r.menuId || '',
        totalWeight: r.totalWeight ?? 0,
        totalMeasureUnitId: r.totalMeasureUnitId || r.totalMeasureUnit?.id || '',
        status: r.status || 'ACTIVE',
        recipeItems: items,
      });

      replace(items);
      console.log('[RecipeModal] ========== RECIPE LOADED ==========');
    }
  }, [isEditing, fullRecipeData, reset, replace]);

  // Auto-calculate total weight when recipe items change
  useEffect(() => {
    if (watchRecipeItems && watchRecipeItems.length > 0) {
      const totalWeight = watchRecipeItems.reduce((sum, item) => {
        const quantity = Number(item?.quantity) || 0;
        return sum + quantity;
      }, 0);
      setValue('totalWeight', totalWeight);
    } else {
      setValue('totalWeight', 0);
    }
  }, [watchRecipeItems, setValue]);

  const createMutation = useMutation({
    mutationFn: (data: CreateRecipeRequest) => dietService.createRecipe(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create recipe'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateRecipeRequest) => dietService.updateRecipe(recipe!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update recipe'),
  });

  const onSubmit = (data: CreateRecipeRequest) => {
    setError(null);

    // Validate that menuId is set
    if (!data.menuId) {
      setError('Please select a diet and menu before saving');
      return;
    }

    console.log('[RecipeModal] Submitting recipe with data:', data);
    isEditing ? updateMutation.mutate(data) : createMutation.mutate(data);
  };
  const isLoading = createMutation.isPending || updateMutation.isPending;


  const diets = (dietsData as any)?.diets || [];
  const menus: Menu[] = (menusData as any)?.menus || [];
  const measureUnits: MeasureUnit[] = measureUnitsData?.measureUnits || [];

  // Get diet ingredients from the selected menu's diet (if any)
  const dietIngredients = useMemo<string[]>(() => {
    const diet = (selectedDietData as any)?.diet as any;
    if (!diet) return [] as string[];
    return (diet.dietIngredients?.filter((di: any) => di.type === 'INCLUDED').map((di: any) => di.ingredientId) || []) as string[];
  }, [selectedDietData]);

  // Add selected diet ingredients to recipe
  const handleAddDietIngredients = () => {
    selectedDietIngredients.forEach(ingredientId => {
      if (!fields.some(f => f.ingredientId === ingredientId)) {
        append({ ingredientId, quantity: 1, measureUnitId: undefined });
      }
    });
    setSelectedDietIngredients([]);
  };

  const ingredientOptions = useMemo(() => {
    const ingredients = ingredientsData?.ingredients || [];
    return ingredients.map(i => ({ value: i.id, label: i.name + (i.description ? ` - ${i.description}` : '') }));
  }, [ingredientsData?.ingredients]);

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Recipe' : 'Create Recipe'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  {/* Core fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Name *</label>
                      <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter recipe name" />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Diet *</label>
                      <select
                        value={selectedDietId}
                        onChange={(e) => {
                          const newDietId = e.target.value;
                          console.log('[RecipeModal] Diet changed to:', newDietId);
                          setSelectedDietId(newDietId);
                          setSelectedMenuId(''); // Reset menu when diet changes
                          setValue('menuId', '');
                        }}
                        className="form-input"
                        required
                      >
                        <option value="">Select a diet...</option>
                        {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                      {!selectedDietId && <p className="text-xs text-gray-500 mt-1">Please select a diet first</p>}
                    </div>
                    <div>
                      <label className="form-label">Menu *</label>
                      <select
                        {...register('menuId', { required: 'Menu is required' })}
                        value={selectedMenuId}
                        onChange={(e) => {
                          const newMenuId = e.target.value;
                          console.log('[RecipeModal] Menu changed to:', newMenuId);
                          setSelectedMenuId(newMenuId);
                          setValue('menuId', newMenuId);
                        }}
                        className="form-input"
                        disabled={!selectedDietId}
                      >
                        <option value="">Select a menu...</option>
                        {menus.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      {errors.menuId && <p className="form-error">{errors.menuId.message}</p>}
                      {selectedDietId && menus.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">No menus found for this diet. Please create a menu first.</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">Description</label>
                      <textarea {...register('description')} className="form-input" rows={2} placeholder="Optional description" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">Instructions</label>
                      <textarea {...register('instructions')} className="form-input" rows={3} placeholder="Optional instructions" />
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="form-input">
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                  </div>

                  {/* Diet Ingredients Section (from selected menu's diet) */}
                  {selectedMenuId && dietIngredients.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Diet Ingredients</h4>
                      <div className="max-h-[250px] overflow-y-auto border border-gray-200 rounded mb-3">
                        <table className="w-full">
                          <tbody>
                            {dietIngredients.map((ingredientId: string) => {
                              const ingredient = ingredientsData?.ingredients?.find(i => i.id === ingredientId);
                              return (
                                <tr key={ingredientId} className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-2 w-8">
                                    <input
                                      type="checkbox"
                                      checked={selectedDietIngredients.includes(ingredientId)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedDietIngredients([...selectedDietIngredients, ingredientId]);
                                        } else {
                                          setSelectedDietIngredients(selectedDietIngredients.filter(id => id !== ingredientId));
                                        }
                                      }}
                                      className="form-checkbox"
                                    />
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700">
                                    {ingredient?.name || 'Unknown'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <Button
                        type="button"
                        onClick={handleAddDietIngredients}
                        disabled={selectedDietIngredients.length === 0}
                        className="w-full"
                      >
                        <PlusIcon className="w-5 h-5 mr-2" /> Add Selected Ingredients
                      </Button>
                    </div>
                  )}

                  {/* Items editor */}
                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">Recipe Items</h4>
                      <Button type="button" onClick={() => append({ ingredientId: '', quantity: 1, measureUnitId: undefined })}>
                        <PlusIcon className="w-5 h-5 mr-2" /> Add item
                      </Button>
                    </div>
                    {isLoadingRecipe && isEditing ? (
                      <div className="text-sm text-gray-500 py-4">Loading recipe items...</div>
                    ) : (
                    <div className="space-y-3">
                      {fields.length === 0 && (
                        <p className="text-sm text-gray-500">No items yet. Click "Add item" to start.</p>
                      )}
                      {fields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-12 gap-3 items-end">
                          <div className="col-span-12 md:col-span-6">
                            <label className="form-label">Ingredient *</label>
                            <select {...register(`recipeItems.${index}.ingredientId` as const, { required: 'Ingredient is required' })} className="form-input">
                              <option value="">Select ingredient</option>
                              {ingredientOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {errors.recipeItems?.[index]?.ingredientId && (
                              <p className="form-error">{String(errors.recipeItems?.[index]?.ingredientId?.message)}</p>
                            )}
                          </div>
                          <div className="col-span-6 md:col-span-3">
                            <label className="form-label">Weight *</label>
                            <input type="number" step="0.01" className="form-input" {...register(`recipeItems.${index}.quantity` as const, { required: 'Weight is required', valueAsNumber: true, min: { value: 0, message: 'Must be >= 0' } })} />
                            {errors.recipeItems?.[index]?.quantity && (
                              <p className="form-error">{String(errors.recipeItems?.[index]?.quantity?.message)}</p>
                            )}
                          </div>
                          <div className="col-span-5 md:col-span-2">
                            <label className="form-label">Unit</label>
                            <select className="form-input" {...register(`recipeItems.${index}.measureUnitId` as const)}>
                              <option value="">None</option>
                              {measureUnits.map(mu => <option key={mu.id} value={mu.id}>{mu.abbreviation || mu.name}</option>)}
                            </select>
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <button type="button" onClick={() => remove(index)} className="text-red-600 hover:text-red-800">
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                  {/* Total Weight and Unit */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t mt-3">
                    <div>
                      <label className="form-label">Total Weight * (Auto-calculated)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input bg-gray-100"
                        placeholder="0.00"
                        readOnly
                        {...register('totalWeight', { required: 'Total weight is required', valueAsNumber: true, min: { value: 0, message: 'Must be >= 0' } })}
                      />
                      {errors as any && (errors as any).totalWeight && (
                        <p className="form-error">{String((errors as any)?.totalWeight?.message)}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Unit *</label>
                      <select className="form-input" {...register('totalMeasureUnitId', { required: 'Unit is required' })}>
                        <option value="">Select unit</option>
                        {measureUnits.map(mu => (
                          <option key={mu.id} value={mu.id}>{mu.abbreviation || mu.name}</option>
                        ))}
                      </select>
                      {(errors as any) && (errors as any).totalMeasureUnitId && (
                        <p className="form-error">{String((errors as any)?.totalMeasureUnitId?.message)}</p>
                      )}
                    </div>
                  </div>


                  <div className="flex justify-end space-x-3 pt-2">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                  </div>
                </form>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RecipeModal;

